import React, { useState, useEffect } from 'react';
import { ViewState, Project, CompanyStats, Career, CarouselItem, BrandingSettings, AboutSettings, Speciality, ContactSettings, Brand } from './types';
import { DEFAULT_COMPANY_STATS, DEFAULT_BRANDING_SETTINGS, DEFAULT_ABOUT_SETTINGS, DEFAULT_CONTACT_SETTINGS, PROJECTS, CAREERS, INITIAL_CAROUSEL_ITEMS, DEFAULT_SPECIALITIES } from './constants';
import Header from './components/Header';
import Footer from './components/Footer';
import Hero from './components/Hero';
import Logo from './components/Logo';
import ProjectSection from './components/ProjectSection';
import CareerSection from './components/CareerSection';
import ContactSection from './components/ContactSection';
import AboutSection from './components/AboutSection';
import AIAdvisor from './components/AIAdvisor';
import AdminDashboard from './components/AdminDashboard';
import BrandsSection from './components/BrandsSection';
import BrandsMarquee from './components/BrandsMarquee';
import SpecialitiesSection from './components/SpecialitiesSection';
import Login from './components/Login';
import { authService } from './authService';
import { dbService } from './dbService';

const App: React.FC = () => {
  const [currentView, setCurrentView] = useState<ViewState>('home');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  const [projects, setProjects] = useState<Project[]>([]);
  const [careers, setCareers] = useState<Career[]>([]);
  const [carouselItems, setCarouselItems] = useState<CarouselItem[]>([]);
  const [companyStats, setCompanyStats] = useState<CompanyStats>(DEFAULT_COMPANY_STATS);
  const [branding, setBranding] = useState<BrandingSettings>(DEFAULT_BRANDING_SETTINGS);
  const [aboutSettings, setAboutSettings] = useState<AboutSettings>(DEFAULT_ABOUT_SETTINGS);
  const [specialities, setSpecialities] = useState<Speciality[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [contactSettings, setContactSettings] = useState<ContactSettings>(DEFAULT_CONTACT_SETTINGS);

  const [loginError, setLoginError] = useState<string | null>(null);

  useEffect(() => {
    const handleHashChange = () => {
      if (window.location.hash === '#admin') {
        setCurrentView('admin');
      } else if (window.location.hash === '#home') {
        setCurrentView('home');
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    handleHashChange();

    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  useEffect(() => {
    const unsubscribe = authService.onAuthUpdate((user) => {
      setIsLoggedIn(!!user);
      if (user) setLoginError(null);
    });
    return () => unsubscribe();
  }, []);

  const fetchData = async () => {
    try {
      const [cloudProjects, cloudCareers, cloudCarousel, cloudStats, cloudBranding, cloudAbout, cloudSpecialities, cloudContact, cloudBrands] = await Promise.all([
        dbService.getProjects().catch(() => null),
        dbService.getCareers().catch(() => null),
        dbService.getCarousel().catch(() => null),
        dbService.getStats().catch(() => null),
        dbService.getBranding().catch(() => null),
        dbService.getAbout().catch(() => null),
        dbService.getSpecialities().catch(() => null),
        dbService.getContact().catch(() => null),
        dbService.getBrands().catch(() => null)
      ]);

      if (cloudProjects && cloudProjects.length > 0) setProjects(cloudProjects);
      else if (cloudProjects === null) setProjects(PROJECTS);
      else setProjects([]); // Cloud is explicitly empty

      if (cloudCareers && cloudCareers.length > 0) setCareers(cloudCareers);
      else if (cloudCareers === null) setCareers(CAREERS);
      else setCareers([]); // Cloud is explicitly empty

      if (cloudCarousel && cloudCarousel.length > 0) setCarouselItems(cloudCarousel);
      else if (cloudCarousel === null) setCarouselItems(INITIAL_CAROUSEL_ITEMS);
      else setCarouselItems([]);

      if (cloudStats) setCompanyStats(cloudStats);
      if (cloudBranding) setBranding(cloudBranding);
      if (cloudAbout) setAboutSettings(cloudAbout);
      if (cloudContact) setContactSettings(cloudContact);
      
      if (cloudSpecialities && cloudSpecialities.length > 0) setSpecialities(cloudSpecialities);
      else if (cloudSpecialities === null) setSpecialities(DEFAULT_SPECIALITIES);
      else setSpecialities([]);

      if (cloudBrands && cloudBrands.length > 0) setBrands(cloudBrands);
      else setBrands([]);

    } catch (error: any) {
      console.warn("Infrastructure Sync Issue - Defaulting to local assets.");
      setProjects(PROJECTS);
      setCareers(CAREERS);
      setCarouselItems(INITIAL_CAROUSEL_ITEMS);
    } finally {
      setTimeout(() => setIsLoading(false), 500);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleLogin = async () => {
    try {
      setLoginError(null);
      const userRecord = await authService.login();
      
      if (userRecord && !userRecord.isApproved) {
        setLoginError('Your account is pending approval from an administrator.');
        await authService.logout();
      }
    } catch (error: any) {
      setLoginError(error.message || 'Authentication failed.');
    }
  };

  const handleLogout = async () => {
    await authService.logout();
    setCurrentView('home');
  };

  const renderContent = () => {
    if (currentView === 'admin') {
      if (!isLoggedIn) {
        return <Login onLogin={handleLogin} error={loginError || undefined} />;
      }
      return (
        <AdminDashboard 
          projects={projects} 
          onUpdateProjects={setProjects}
          careers={careers}
          onUpdateCareers={setCareers}
          carouselItems={carouselItems}
          onUpdateCarousel={setCarouselItems}
          companyStats={companyStats}
          onUpdateStats={setCompanyStats}
          branding={branding}
          onUpdateBranding={setBranding}
          specialities={specialities}
          onUpdateSpecialities={setSpecialities}
          brands={brands}
          onUpdateBrands={setBrands}
          aboutSettings={aboutSettings}
          onUpdateAbout={setAboutSettings}
          contactSettings={contactSettings}
          onUpdateContact={setContactSettings}
          onLogout={handleLogout}
        />
      );
    }

    if (isLoading) {
      return (
        <div className="fixed inset-0 bg-white flex flex-col items-center justify-center z-[9999]">
          <div className="relative flex flex-col items-center">
            {/* Animated Logo */}
            <div className="mb-8 animate-bounce">
              <Logo className="h-16 w-auto" customSrc={branding.logoUrl} />
            </div>
            
            {/* Progress Bar Container */}
            <div className="w-64 h-1 bg-slate-100 rounded-full overflow-hidden relative">
              {/* Animated Progress Bar */}
              <div className="absolute inset-0 bg-blue-700 rounded-full animate-loading-bar"></div>
            </div>
            
            <div className="mt-6 text-[10px] font-black uppercase tracking-[0.4em] text-slate-400 animate-pulse">
              Initializing Infrastructure
            </div>
          </div>
        </div>
      );
    }

    switch (currentView) {
      case 'home':
        return (
          <>
            <Hero setView={setCurrentView} customSrc={branding.heroImageUrl} />
            
            {/* PERFORMANCE COUNTERS - SECURED IN A SINGLE ROW */}
            <div className="bg-slate-50 dark:bg-slate-900/50 py-12 px-4 border-b border-slate-100 dark:border-slate-800">
              <div className="max-w-7xl mx-auto grid grid-cols-3 gap-2 md:gap-6">
                {[
                  { val: companyStats.yearsExperience + '+', label: 'Years' },
                  { val: companyStats.projectsCompleted + '+', label: 'Projects' },
                  { val: companyStats.kilometersBuilt, label: 'KMs Built' }
                ].map((s, i) => (
                  <div key={i} className="py-6 md:py-10 bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700 text-center flex flex-col justify-center transition-transform hover:scale-[1.02]">
                    <div className="text-xl md:text-4xl font-black text-blue-900 dark:text-blue-400 mb-1 leading-none">{s.val}</div>
                    <div className="text-[7px] md:text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>

            <SpecialitiesSection specialities={specialities} />

            <ProjectSection limit={2} setView={setCurrentView} projects={projects} />
            <BrandsMarquee brands={brands} />
            <AIAdvisor compact />
          </>
        );
      case 'about':
        return <AboutSection settings={aboutSettings} />;
      case 'projects':
        return <ProjectSection setView={setCurrentView} projects={projects} carouselItems={carouselItems} />;
      case 'careers':
        return <CareerSection careers={careers} />;
      case 'clients':
        return <BrandsSection brands={brands} />;
      case 'contact':
        return <ContactSection settings={contactSettings} />;
      case 'ai-advisor':
        return <AIAdvisor />;
      default:
        return <Hero setView={setCurrentView} customSrc={branding.heroImageUrl} />;
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white text-slate-900 selection:bg-blue-100">
      <Header 
        currentView={currentView} 
        setView={setCurrentView} 
        customLogoUrl={branding.logoUrl} 
        isLoggedIn={isLoggedIn}
        onLogout={handleLogout}
      />
      <main className="flex-grow pt-16">
        {renderContent()}
      </main>
      <Footer settings={contactSettings} />
    </div>
  );
};

export default App;